<template>
  <div class="orcamento-servico-table">
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="tableEndpoint"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="(e: any) => handleRowClick(e)"
      @edit-click="handleEditClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type ServicoId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
}

interface OrcamentoServicoRow {
  orcamento_servico_id?: ServicoId;
  servico_label?: string;
  servico_valor?: number;
  orcamento_servico_qtd?: number;
  orcamento_servico_tipo?: number | string;
  orcamento_servico_status?: number | string;
  [key: string]: unknown;
}

const props = withDefaults(defineProps<{
  orcamentoId?: number;
}>(), {
  orcamentoId: 0
});

const emit = defineEmits<{
  'edit-servico': [servicoId: ServicoId];
}>();

const { t } = useI18n();

const dataTableRef = ref<DataTableInstance | null>(null);

const tableEndpoint = computed<string | null>(() => {
  if (!props.orcamentoId || props.orcamentoId === 0) {
    return null;
  }
  return `/index.php/orcamentos_servicos/buscaOrcamentoServicosPorOrcamentoID/${props.orcamentoId}`;
});

const tableConfig = computed(() => {
  return {
    tableId: 'orcamentoServicoTable',
    pageLength: 25,
    pagination: true,
    columns: tableColumns.value,
    heightMode: 'fill-viewport',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.orcamento_servico_id;
      return id == null ? '' : `orcamentoservico-row-${id}`;
    }
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'orcamento_servico_id',
      title: t('ID'),
      renderType: 'clickable_id'
    },
    {
      data: 'servico_label',
      title: t('Serviço')
    },
    {
      data: 'servico_valor',
      title: t('Valor'),
      renderType: 'currency'
    },
    {
      data: 'orcamento_servico_qtd',
      title: t('Quantidade')
    },
    {
      data: 'orcamento_servico_tipo',
      title: t('Tipo'),
      cellRenderer: (params: CellParams) => {
        const data = params.value;
        if (data == 1) {
          return t('Agendado no orçamento');
        } else {
          return t('Agendado no compromisso');
        }
      }
    },
    {
      data: 'orcamento_servico_status',
      title: t('Status'),
      cellRenderer: (params: CellParams) => {
        const data = params.value;
        // 1 Aguardando aprovação do orçamento, 2 Aguardando agendamento, 3 Serviço agendado, 4 Serviço realizado, 5 serviço cancelado
        if (data == 1) {
          return t('Aguardando aprovação do orçamento');
        } else if (data == 2) {
          return t('Aguardando agendamento');
        } else if (data == 3) {
          return t('Serviço agendado');
        } else if (data == 4) {
          return t('Serviço realizado');
        } else if (data == 5) {
          return t('Serviço cancelado');
        }
        return data;
      }
    }
  ];
});

const loadData = (): void => {
  log('OrcamentoServicoTable', 'loadData', 'Triggering grid refresh');
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('OrcamentoServicoTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
};

const handleDataError = (error: unknown): void => {
  log('OrcamentoServicoTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar serviços'));
};

const handleRowClick = (rowData?: OrcamentoServicoRow): void => {
  log('OrcamentoServicoTable', 'handleRowClick', 'Row clicked', rowData?.orcamento_servico_id || null);
};

const handleEditClick = (servicoId: ServicoId): void => {
  log('OrcamentoServicoTable', 'handleEditClick', 'Edit clicked', servicoId);
  emit('edit-servico', servicoId);
};


const autoSizeColumns = (): void => {
  log('OrcamentoServicoTable', 'autoSizeColumns', 'Auto-sizing columns (DataTable handles this automatically)');
  // DataTable handles column sizing automatically, no action needed
};

const onTableVisible = (): void => {
  log('OrcamentoServicoTable', 'onTableVisible', 'Table became visible');
  // DataTable handles column sizing automatically, no action needed
};

// Lifecycle
onMounted(() => {
  log('OrcamentoServicoTable', 'mounted', 'Component mounted');
  
  eventBus.on('orcamentoservico.saved', handleServicoSaved);
});

const handleServicoSaved = (): void => {
  log('OrcamentoServicoTable', 'event:orcamentoservico.saved', 'Received');
  loadData();
};

onBeforeUnmount(() => {
  log('OrcamentoServicoTable', 'beforeUnmount', 'Component unmounting');
  eventBus.off('orcamentoservico.saved', handleServicoSaved);
});

// Expose methods
defineExpose({
  loadData,
  autoSizeColumns,
  onTableVisible
});
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>
